import { Box, Typography } from '@evaneos/b2b-ui';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useParams } from 'react-router-dom';

import { MessageAttachment } from '../../../../../resources/discussion/types';

import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';

import { DossierId } from '../../../../../resources/dossier/types';

import { PhotoCard } from './PhotoCard';
import { PhotoDetailModal } from './PhotoDetailModal';

type PhotosSectionProps = {
    photos: MessageAttachment[];
    getMessageInfo: (
        messageId?: number | null,
    ) => { authorName?: string; authorId?: string; sendDate?: Date } | undefined;
};

export const PhotosSection = ({ photos, getMessageInfo }: PhotosSectionProps) => {
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { formatMessage } = useIntl();
    const [selectedPhoto, setSelectedPhoto] = useState<MessageAttachment | null>(null);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    if (photos.length === 0) return null;

    const onDownloadLinkClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.attachmentDownloaded({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: '600' }} color="text.secondary">
                {formatMessage({ id: 'discussion.attachments_list.photos_subsection_title' })}
            </Typography>
            <Box component="ul" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 0, m: 0 }}>
                {photos.map((attachment) => (
                    <PhotoCard
                        key={attachment.link}
                        attachment={attachment}
                        onOpen={() => setSelectedPhoto(attachment)}
                        onDownloadLinkClick={onDownloadLinkClick}
                    />
                ))}
            </Box>

            {selectedPhoto && (
                <PhotoDetailModal
                    attachment={selectedPhoto}
                    messageInfo={getMessageInfo(selectedPhoto.messageId)}
                    onClose={() => setSelectedPhoto(null)}
                    onDownloadLinkClick={onDownloadLinkClick}
                />
            )}
        </Box>
    );
};
